import { generatePath, useNavigate } from 'react-router';
import { AdditionalDetails, LargeShiftDetails, Receipt } from '@tempmee/react-blocks';
import { Link } from '@tempmee/react';
import { useMediaQuery } from '~/hooks';
import { routes } from '~/routes';
import { COLLAPSE_BREAKPOINT, DetailsPageShell } from '~/components/shared/DetailsPage';
import type { IShiftDetailsPageProps } from '../shiftDetailsPages';
import { toAdditionalDetailsRows } from '../shared/utils/shiftPanelsProps';
import { EM_DASH } from '~/utils/String';
import { getProfessionalName } from '~/utils/professional';
import { toShiftSummaryFields } from '../shared/utils/shiftSummaryProps';
import { CancelledProfessionalPanel } from './components/CancelledProfessionalPanel';
import { ShiftUnfilledPanel } from './components/ShiftUnfilledPanel';
import { useBookedProfessionalCredentials } from '../shared/hooks/useBookedProfessionalCredentials';
import { hasTransactionHistory } from '../shared/utils/transactionHistory';
import {
  hasCancellationReceipt,
  resolveCancelledExpiredVariant,
  isExpiredShift,
  toCancellationReceiptLines,
  toCancellationRefundTotal,
  toPageTitle,
  toShiftProfessional,
  toShiftStatus,
  toUnfilledDescription,
  wasRepostedAfterCancellation,
} from './utils/cancelledExpiredProps';

/**
 * CANCELLED / EXPIRED — the redesigned Shift Details page for a shift that
 * ended without being worked.
 *
 * One component covers all four Figma frames. They share the entire sidebar and
 * differ only in the primary column and the page's copy, so the page resolves a
 * variant (see `resolveCancelledExpiredVariant`) and hands it to one of two
 * panels. Four near-identical page components would have made the sidebar four
 * things to keep in step.
 *
 * These are terminal states: the office is here to understand what happened, not
 * to act. So the page offers no shift actions — the shell's three-dot trigger
 * hides itself on an empty `actions` list — and no professional actions, which
 * matches the legacy screen's own gating of favourite/block and cancel on
 * `status !== 'cancelled'`.
 *
 * The legacy `JobDetails` screen is untouched. With the flag off the route still
 * renders it for every status it serves, cancelled and expired included.
 *
 * PostHog: the legacy screen fires no events of its own, so there are none to
 * carry over. The route is unchanged, so the app's automatic `$pageview` still
 * fires here exactly as it did.
 *
 * Onsite interviews never reach it: the registry keeps those on the legacy
 * screen (`shiftDetailsPageForJob`).
 */
export const CancelledExpiredPage = ({ job }: IShiftDetailsPageProps) => {
  const navigate = useNavigate();
  const [isCollapsed] = useMediaQuery([COLLAPSE_BREAKPOINT]);

  const variant = resolveCancelledExpiredVariant(job);
  const professional = toShiftProfessional(job);
  const jobId = job?.id;

  const { authorizations } = useBookedProfessionalCredentials({ job, professional });

  return (
    <DetailsPageShell
      testIDPrefix="shift-details"
      actionsLabel="Shift actions"
      title={toPageTitle(job)}
      onBack={() => navigate(-1)}
      summary={
        <LargeShiftDetails
          status={toShiftStatus(job)}
          {...toShiftSummaryFields(job)}
          data-testid="shift-summary"
        />
      }
      primary={
        variant === 'unfilled' || !professional ? (
          <ShiftUnfilledPanel description={toUnfilledDescription(job)} />
        ) : (
          <CancelledProfessionalPanel
            job={job}
            professional={professional}
            isCancelledByProfessional={variant === 'cancelled-by-professional'}
            wasReposted={wasRepostedAfterCancellation(job)}
            authorizations={authorizations}
            isCollapsed={isCollapsed}
          />
        )
      }
      sidebar={
        <>
          <AdditionalDetails
            variant="cancelled"
            {...toAdditionalDetailsRows(
              job,
              // An expired shift was never cancelled by anyone, so it draws no
              // "Cancelled by" row at all. A real cancellation keeps the row and
              // falls back to an em dash when the payload does not name who.
              isExpiredShift(job)
                ? undefined
                : getProfessionalName(job?.cancelPerformedBy) || EM_DASH,
            )}
            // Read-only on this variant, which is what the block's `cancelled`
            // variant means: the note is a record of what the office asked for,
            // and no endpoint patches it from a shift that has already ended.
            notes={job?.notes_for_temp || undefined}
            defaultOpen={false}
            data-testid="additional-details"
          />

          {hasCancellationReceipt(job) ? (
            <Receipt
              lines={toCancellationReceiptLines(job)}
              total={toCancellationRefundTotal(job)}
              totalAction={
                hasTransactionHistory(job) && jobId ? (
                  // `as="button"` so the click routes in-app: an anchor with an
                  // href would reload the SPA, where the legacy screen pushed
                  // onto history.
                  <Link
                    as="button"
                    onClick={() =>
                      navigate(generatePath(routes.transactionHistoryDetails, { jobId }))
                    }
                    data-testid="transaction-history-link"
                    data-cy="transaction-history-link"
                  >
                    Transaction History
                  </Link>
                ) : undefined
              }
              defaultOpen={false}
              data-testid="cancellation-receipt"
            />
          ) : null}
        </>
      }
    />
  );
};
